import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '@/components/site/Header';
import Footer from '@/components/site/Footer';
import Seo from '@/components/Seo';

const BREADCRUMB = [
  { name: 'Start', path: '/' },
  { name: 'Impressum', path: '/impressum' },
];

const Section = ({ title, children }) => (
  <section className="py-8 border-t border-slate-200/70">
    <h2 className="font-display font-black text-slate-900 text-xl tracking-tight mb-4">
      {title}
    </h2>
    <div className="text-slate-700 text-[15px] leading-relaxed space-y-2">{children}</div>
  </section>
);

const Field = ({ label, value }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-1 sm:gap-4">
    <div className="font-mono-tech text-[11px] uppercase tracking-[0.18em] text-slate-500 sm:pt-1">
      {label}
    </div>
    <div className="text-slate-900">{value}</div>
  </div>
);

export default function Impressum() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-[#f6f7f9] text-slate-900 min-h-screen" data-testid="impressum-page">
      <Seo
        title="Impressum"
        description="Impressum von MIBAX Messtechnik – Inhaber M. Basic, Tal 44, 80331 München. Angaben gemäß § 5 DDG und § 18 Abs. 2 MStV."
        path="/impressum"
        breadcrumb={BREADCRUMB}
      />
      <Header />

      <main className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0891b2] mb-8"
            data-testid="impressum-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
          </Link>

          <div className="eyebrow mb-5">Rechtliche Hinweise</div>
          <h1 className="font-display font-black text-slate-900 tracking-tighter text-5xl sm:text-6xl leading-[0.95] mb-3">
            Impressum
          </h1>
          <p className="text-slate-500 text-base">
            Angaben gemäß § 5 DDG (vormals TMG) und § 18 Abs. 2 MStV.
          </p>

          <Section title="Anbieter / Diensteanbieter">
            <Field label="Firma" value="MIBAX Messtechnik e.K." />
            <Field
              label="Anschrift"
              value={
                <>
                  Tal 44<br />
                  80331 München<br />
                  Deutschland
                </>
              }
            />
            <Field label="Inhaber" value="M. Basic" />
          </Section>

          <Section title="Handelsregister">
            <Field label="Registergericht" value="Amtsgericht München" />
            <Field label="Registernummer" value="HRA 123088" />
          </Section>

          <Section title="Kontakt">
            <Field
              label="Telefon"
              value={
                <a href="tel:+436769218022" className="hover:text-[#0891b2]">
                  +43 676 9218022
                </a>
              }
            />
            <Field
              label="E-Mail"
              value={
                <a href="mailto:M.Basic@mibax-messtechnik.com" className="hover:text-[#0891b2] break-all">
                  M.Basic@mibax-messtechnik.com
                </a>
              }
            />
            <Field
              label="Web"
              value={
                <a href="https://www.mibax-messtechnik.com" className="hover:text-[#0891b2]">
                  www.mibax-messtechnik.com
                </a>
              }
            />
          </Section>

          <Section title="Umsatzsteuer-Identifikationsnummer">
            <p>
              Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:&nbsp;
              <span className="font-mono-tech text-slate-500">[wird auf Anfrage mitgeteilt]</span>
            </p>
          </Section>

          <Section title="Berufsbezeichnung & berufsrechtliche Regelungen">
            <p>Tätigkeitsfeld: Industrielle Ultraschall-Leckageortung und Energieeffizienz-Analysen für Druckluft- und Prozessgasanlagen.</p>
            <p className="text-slate-500 text-sm">Keine reglementierte Berufstätigkeit im Sinne der GewO.</p>
          </Section>

          <Section title="Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV">
            <p>
              M. Basic<br />
              Tal 44, 80331 München<br />
              Deutschland
            </p>
          </Section>

          <Section title="EU-Streitschlichtung">
            <p>
              Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:&nbsp;
              <a
                href="https://ec.europa.eu/consumers/odr"
                target="_blank"
                rel="noreferrer noopener"
                className="text-[#0891b2] hover:underline"
              >
                https://ec.europa.eu/consumers/odr
              </a>
              .
            </p>
            <p>Unsere E-Mail-Adresse finden Sie oben im Impressum.</p>
          </Section>

          <Section title="Verbraucherstreitbeilegung / Universalschlichtungsstelle">
            <p>
              Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer
              Verbraucherschlichtungsstelle teilzunehmen.
            </p>
          </Section>

          <Section title="Haftung für Inhalte">
            <p>
              Als Diensteanbieter sind wir gemäß § 7 Abs. 1 DDG für eigene Inhalte auf diesen Seiten
              nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 DDG sind wir als
              Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde
              Informationen zu überwachen oder nach Umständen zu forschen, die auf eine
              rechtswidrige Tätigkeit hinweisen.
            </p>
            <p>
              Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den
              allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch
              erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei
              Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte umgehend
              entfernen.
            </p>
          </Section>

          <Section title="Haftung für Links">
            <p>
              Unser Angebot enthält gegebenenfalls Links zu externen Websites Dritter, auf deren
              Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch
              keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige
              Anbieter oder Betreiber der Seiten verantwortlich.
            </p>
            <p>
              Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße
              überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar.
              Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete
              Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von
              Rechtsverletzungen werden wir derartige Links umgehend entfernen.
            </p>
          </Section>

          <Section title="Urheberrecht">
            <p>
              Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten
              unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung,
              Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechts
              bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
              Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen
              Gebrauch gestattet.
            </p>
            <p>
              Soweit die Inhalte auf dieser Seite nicht vom Betreiber erstellt wurden, werden die
              Urheberrechte Dritter beachtet. Insbesondere werden Inhalte Dritter als solche
              gekennzeichnet. Sollten Sie trotzdem auf eine Urheberrechtsverletzung aufmerksam
              werden, bitten wir um einen entsprechenden Hinweis. Bei Bekanntwerden von
              Rechtsverletzungen werden wir derartige Inhalte umgehend entfernen.
            </p>
          </Section>

          <Section title="Bildnachweise & 3D-Modelle">
            <p>
              Alle auf dieser Website dargestellten 3D-Visualisierungen (Akustikkamera,
              Rohrleitungssystem) wurden eigens für MIBAX Messtechnik erstellt und sind
              urheberrechtlich geschützt.
            </p>
          </Section>

          <div className="py-10 mt-6 border-t border-slate-200/70">
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-400">
              Stand: {new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
